import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useSocketContext } from "@/context/socket.context/socket.context";
import { getLeadDetailsUrl } from "@/routes/paths";
import { getLeadInteractions } from "@/services/interactions.service";
import leadsService from "@/services/leads.service";
import { useAuthStore } from "@/store/auth/auth.store";
import { useSmsRefreshStore } from "@/store/sms/sms-refresh.store";

// Local wire strings until @icebergeradmin/common carries them; meetings-back
// emits both to the company room (see its docs/socket-events.md).
export const SOCKET_EVENT_SMS_RECEIVED = "sms.received";
export const SOCKET_EVENT_SMS_STATUS_CHANGED = "sms.status_changed";

/**
 * Ids only: the company socket room is joined without authentication, so the
 * event never carries the text, the number or the name — those are fetched
 * through the authenticated API before the toast shows.
 */
export type TSmsReceivedEvent = {
  leadId: number;
  /** The agent whose line was texted; null for a company-line text. */
  agentUserId: number | null;
  interactionId: number;
};

type TSmsStatusChangedEvent = {
  leadId: number;
  interactionId: number;
  status: string;
};

/** What the toast shows, resolved through the API from the event's ids. */
export type TSmsToast = {
  leadId: number;
  interactionId: number;
  name: string;
  preview: string;
  /** Lead's profile photo URL, when the backend includes it. */
  leadAvatar?: string | null;
};

const AUTO_HIDE_MS = 10_000;
const PREVIEW_CHARS = 120;

const showPushNotification = (toast: TSmsToast) => {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const notification = new Notification(`New text from ${toast.name}`, {
    body: toast.preview,
    icon: "/favicon.ico",
    tag: `sms-${toast.interactionId}`,
  });
  notification.onclick = () => {
    window.focus();
    window.location.href = getLeadDetailsUrl(toast.leadId);
    notification.close();
  };
};

/** The event's ids resolved to a name and a preview; empty strings when the API can't say. */
const resolveToast = async (sms: TSmsReceivedEvent): Promise<TSmsToast> => {
  let name = "";
  let preview = "";
  let leadAvatar: string | null = null;
  try {
    const [lead, interactions] = await Promise.all([
      leadsService.getById(sms.leadId),
      getLeadInteractions(sms.leadId, 1, 5, "sms"),
    ]);
    name = `${lead.firstName ?? ""} ${lead.lastName ?? ""}`.trim();
    leadAvatar = lead.avatar ?? null;
    // Only the exact interaction: in a burst, quoting "the latest inbound" would caption
    // this toast with another message's text.
    const match = interactions.data.find((i) => i.id === sms.interactionId);
    preview = match?.sms?.body?.slice(0, PREVIEW_CHARS) ?? "";
  } catch {
    // The toast still opens the lead; it just can't quote the text.
  }
  return {
    leadId: sms.leadId,
    interactionId: sms.interactionId,
    name: name || "A lead",
    preview: preview || "sent you a text message",
    leadAvatar,
  };
};

/**
 * Global listener for the messaging socket events. Every event refreshes the
 * lead's SMS surfaces; a lead's text additionally notifies the one agent whose
 * line it is (a company-line text just lands on the thread — by decision,
 * nobody is toasted for it).
 */
export const useInboundSmsNotifier = () => {
  const socket = useSocketContext();
  const { enqueueSnackbar } = useSnackbar();
  const userId = useAuthStore((s) => s.user?.id);
  const bump = useSmsRefreshStore((s) => s.bump);

  useEffect(() => {
    const handleReceived = (sms: TSmsReceivedEvent) => {
      // The socket is untyped: a malformed event must not seed the store or navigate to /leads/undefined.
      if (typeof sms?.leadId !== "number" || typeof sms?.interactionId !== "number") return;
      bump(sms.leadId);
      if (!userId || sms.agentUserId !== userId) return;
      void resolveToast(sms).then((toast) => {
        // Hidden tab with push permission → native notification. Without the permission the
        // snackbar still queues, so the text is waiting if they come back within its lifetime.
        if (document.hidden && "Notification" in window && Notification.permission === "granted") {
          showPushNotification(toast);
          return;
        }
        enqueueSnackbar("", {
          variant: "newSmsNotification",
          sms: toast,
          // A webhook replay re-emits the event; one toast per text.
          key: `sms-${toast.interactionId}`,
          preventDuplicate: true,
          autoHideDuration: AUTO_HIDE_MS,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
        });
      });
    };

    const handleStatusChanged = (event: TSmsStatusChangedEvent) => {
      if (typeof event?.leadId !== "number") return;
      bump(event.leadId);
    };

    socket.on(SOCKET_EVENT_SMS_RECEIVED, handleReceived);
    socket.on(SOCKET_EVENT_SMS_STATUS_CHANGED, handleStatusChanged);
    return () => {
      socket.off(SOCKET_EVENT_SMS_RECEIVED, handleReceived);
      socket.off(SOCKET_EVENT_SMS_STATUS_CHANGED, handleStatusChanged);
    };
  }, [socket, enqueueSnackbar, userId, bump]);
};
